import React, { useState, useMemo, useEffect } from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { useDispatch, useSelector } from "react-redux";
import history from "@history";
import { useTranslation } from "react-i18next";
import useAuthority from "../../../hooks/useAuthority";
import { useTableRowStyles } from "./multiline-table.styles";
import {
  FlightRequestBody,
  PagedFilghtsList,
} from "app/types/service-types/FlightTypes";
import LoadingTableRow from "../base-table/LoadingTableRow";
import NoDataTableRow from "../base-table/NoDataTableRow";
import ModalDialog from "../modal-dialog/ModalDialog";
import { deleteFlight } from "../../flights/store/flights-list/delete-flight.slice";
import MultilineTableCell from "./MultilineTableCell";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import DateUtils from "../../../utils/DateUtils";
import TableActionButtons from "../base-table/TableActionButtons";
import { TableActionsTypes } from "../../../types/TableTypes";
import { ROLES } from "../../../constants/roles";
import { filterFlights } from "../../flights/store/flights-list/filter-flights.slice";
import { AppState } from "../../../store";
import {
  Tooltip,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@material-ui/core";

/** ------------------------------
 *  Helpers for money / load factor
 *  ------------------------------ */
const formatMoney = (value: number | null | undefined, currency = "€") => {
  if (value == null || Number.isNaN(value)) return `0 ${currency}`;
  try {
    return `${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  } catch {
    return `${value} ${currency}`;
  }
};

const parseMoneyString = (raw: unknown): number | null => {
  if (raw == null) return null;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const n = parseFloat(raw.replace(/[^\d.-]/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const getAdultPriceWithTax = (row: FlightRequestBody): number | null => {
  const anyRow = row as any;
  const fromLowest = parseMoneyString(anyRow?.lowestPriceWithTax);
  if (fromLowest != null) return fromLowest;

  const base =
    Number(anyRow?.availableSectorPrice?.price?.oneWayPrices?.forAdults) || null;
  const tax = Number(anyRow?.taxOfFlight) || 0;
  if (base != null) return base + tax;
  return null;
};

const loadFactor = (totalSeats: number, reservedSeats: number): number => {
  const total = Math.max(0, Number(totalSeats) || 0);
  const reserved = Math.max(0, Math.min(total, Number(reservedSeats) || 0));
  if (total === 0) return 0;
  return Math.round((reserved / total) * 100);
};

type DateOp = "eq" | "gte" | "lte";
const DATE_OPS: Array<{ value: DateOp; label: string }> = [
  { value: "eq", label: "=" },
  { value: "gte", label: "≥" },
  { value: "lte", label: "≤" },
];

const isHHmm = (s?: string) => !s || /^([01]\d|2[0-3]):([0-5]\d)$/.test(s);
const ssKey = "flights.filters.session";

interface IProps {
  data: PagedFilghtsList | null;
  loading: boolean;
}

const MultilineTable = (props: IProps) => {
  const classes = useTableRowStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { hasUserAnyAuthority } = useAuthority();

  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState<{
    state: boolean;
    deleteId: string;
    flightName?: string;
  }>({
    state: false,
    deleteId: "",
    flightName: "",
  });

  const filters = useSelector(
    (state: AppState) => state.flights.flightsList.filterFlights
  ) as any;

  const [advOpen, setAdvOpen] = useState(false);
  const [dateOp, setDateOp] = useState<DateOp>("eq");
  const [form, setForm] = useState<any>({
    flightNumber: "",
    originDestinationPair: "",
    departureTimeFrom: "",
    departureTimeTo: "",
    totalSeatsMin: "",
    totalSeatsMax: "",
    adultsChdMin: "",
    adultsChdMax: "",
    infantsMin: "",
    infantsMax: "",
    airlineCompanyIdsText: "",
    date: "",
  });

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ssKey);
      if (raw) {
        const saved = JSON.parse(raw);
        setForm((f: any) => ({
          ...f,
          ...saved.__modalMirror,
        }));
        if (saved.__dateOp) setDateOp(saved.__dateOp as DateOp);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const snapshot = {
        ...filters,
        __modalMirror: form,
        __dateOp: dateOp,
      };
      sessionStorage.setItem(ssKey, JSON.stringify(snapshot));
    } catch {}
  }, [form, dateOp, filters]);

  const set = <K extends keyof typeof form>(k: K, v: any) =>
    setForm((p: any) => ({ ...p, [k]: v }));

  const buildDateParams = (date?: string): { fromDate?: string; toDate?: string } => {
    if (!date) return {};
    if (dateOp === "eq") return { fromDate: date, toDate: date };
    if (dateOp === "gte") return { fromDate: date };
    return { toDate: date };
  };

  const clearAllFilters = () => {
    dispatch(filterFlights({ page: 0 } as any));
    setForm({
      flightNumber: "",
      originDestinationPair: "",
      departureTimeFrom: "",
      departureTimeTo: "",
      totalSeatsMin: "",
      totalSeatsMax: "",
      adultsChdMin: "",
      adultsChdMax: "",
      infantsMin: "",
      infantsMax: "",
      airlineCompanyIdsText: "",
      date: "",
    });
    setDateOp("eq");
    try {
      sessionStorage.removeItem(ssKey);
    } catch {}
  };

  const applyAdvanced = () => {
    if (!isHHmm(form.departureTimeFrom) || !isHHmm(form.departureTimeTo)) {
      window.alert("Koha duhet të jetë në formatin HH:mm (p.sh. 06:00).");
      return;
    }

    const airlineCompanyIds = (form.airlineCompanyIdsText || "")
      .split(/[,\s]+/)
      .map((s: string) => s.trim())
      .filter(Boolean);

    const dateParams = buildDateParams(form.date);

    const payload: any = {
      ...filters,
      page: 0,
      flightNumber: form.flightNumber || undefined,
      originDestinationPair: form.originDestinationPair || undefined,
      departureTimeFrom: form.departureTimeFrom || undefined,
      departureTimeTo: form.departureTimeTo || undefined,
      totalSeatsMin:
        form.totalSeatsMin === "" ? undefined : Number(form.totalSeatsMin),
      totalSeatsMax:
        form.totalSeatsMax === "" ? undefined : Number(form.totalSeatsMax),
      adultsChdMin:
        form.adultsChdMin === "" ? undefined : Number(form.adultsChdMin),
      adultsChdMax:
        form.adultsChdMax === "" ? undefined : Number(form.adultsChdMax),
      infantsMin: form.infantsMin === "" ? undefined : Number(form.infantsMin),
      infantsMax: form.infantsMax === "" ? undefined : Number(form.infantsMax),
      ...(airlineCompanyIds.length ? { airlineCompanyIds } : {}),
      ...dateParams,
    };

    dispatch(filterFlights(payload));
    setAdvOpen(false);
  };

  const activeChips = useMemo(() => {
    const chips: string[] = [];
    if (filters?.flightNumber) chips.push(`# ${filters.flightNumber}`);
    if (filters?.originDestinationPair) chips.push(filters.originDestinationPair);
    if (filters?.departureTimeFrom || filters?.departureTimeTo)
      chips.push(`Time ${filters.departureTimeFrom ?? "…"}–${filters.departureTimeTo ?? "…"}`);
    if (filters?.fromDate || filters?.toDate)
      chips.push(`Date ${filters.fromDate ?? "…"}–${filters.toDate ?? "…"}`);
    if (filters?.totalSeatsMin || filters?.totalSeatsMax)
      chips.push(`Seats ${filters.totalSeatsMin ?? "…"}–${filters.totalSeatsMax ?? "…"}`);
    if (filters?.adultsChdMin || filters?.adultsChdMax)
      chips.push(`Adult+CHD ${filters.adultsChdMin ?? "…"}–${filters.adultsChdMax ?? "…"}`);
    if (filters?.infantsMin || filters?.infantsMax)
      chips.push(`INF ${filters.infantsMin ?? "…"}–${filters.infantsMax ?? "…"}`);
    if (filters?.airlineCompanyIds?.length)
      chips.push(`Airlines ${filters.airlineCompanyIds.length}`);
    return chips;
  }, [filters]);

  const toggleOpen = () => setOpen(!open);

  const tableActions: TableActionsTypes = {
    onEditRow: (rowId: number) => {
      history.push({
        pathname: `/flightsedit/${rowId}/edit`,
        state: { id: rowId },
      });
    },
    onDeleteRow: (rowId: string, name: string) => {
      setModalOpen({ state: true, deleteId: rowId, flightName: name });
    },
    onListOfReservation: (
      rowId: string,
      _name: string,
      flightData: FlightRequestBody
    ) => {
      history.push({
        pathname: `/flightsedit/${rowId}/reservations`,
        state: { flight: flightData, id: rowId },
      });
    },
    onViewSeatMap: (rowId: string) => {
      history.push(`/flights/seat-map/${rowId}`);
    },
  };

  const onOkModal = () => {
    const deletedId = modalOpen.deleteId;

    setModalOpen({
      state: false,
      deleteId: "",
      flightName: "",
    });

    dispatch(deleteFlight({ id: deletedId }) as any)
      .then(() => {
        dispatch(
          filterFlights({
            ...filters,
            page: 0,
          }) as any
        );
      })
      .catch((error: any) => {
        console.error("Delete failed", error);
      });
  };

  const ADMIN_COLS = 12;
  const NON_ADMIN_COLS = 3;

  return (
    <TableContainer component={Paper}>
      <Box display="flex" alignItems="center" justifyContent="space-between" p={2} pt={2} pb={1}>
        <Box>
          <Button variant="outlined" onClick={() => setAdvOpen(true)}>
            {t("app.main.flights.searchPanel.button.advanced", "Advanced filters")}
          </Button>
          <Button style={{ marginLeft: 8 }} variant="text" onClick={clearAllFilters}>
            {t("app.main.schedules.searchPanel.button.clear", "Clear All")}
          </Button>
        </Box>
        <Box display="flex" flexWrap="wrap" gridGap={8}>
          {activeChips.map((c, i) => (
            <Chip key={i} label={c} />
          ))}
          {activeChips.length === 0 && (
            <span style={{ opacity: 0.6 }}>
              {t("app.main.shared.noActiveFilters", "No active filters")}
            </span>
          )}
        </Box>
      </Box>

      <Dialog open={advOpen} onClose={() => setAdvOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t("app.main.flights.filters.title", "Filter flights")}</DialogTitle>
        <DialogContent dividers>
          <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gridGap={16}>
            <Box gridColumn="span 2">
              <TextField
                select
                label={t("app.main.flights.filters.dateOp", "Date op")}
                value={dateOp}
                onChange={(e) => setDateOp(e.target.value as DateOp)}
                fullWidth
                variant="outlined"
                size="small"
              >
                {DATE_OPS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box gridColumn="span 4">
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  label={t("app.main.schedules.searchPanel.datepicker.from", "Date")}
                  format="dd/MM/yyyy"
                  value={form.date ? new Date(form.date) : null}
                  onChange={(date: Date | null) =>
                    set("date", date ? date.toISOString().slice(0, 10) : "")
                  }
                  inputVariant="outlined"
                  size="small"
                  fullWidth
                  autoOk
                  disableToolbar
                  variant="inline"
                />
              </MuiPickersUtilsProvider>
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.flightNumber", "Flight number")}
                placeholder="e.g., IV5251"
                value={form.flightNumber}
                onChange={(e) => set("flightNumber", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.routePair", "Origin–Destination")}
                placeholder="PRN-ZRH"
                value={form.originDestinationPair}
                onChange={(e) => set("originDestinationPair", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                helperText={t(
                  "app.main.flights.filters.routePair.helper",
                  "Directional (MUC-PRN ≠ PRN-MUC)"
                )}
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.timeFrom", "Time from (HH:mm)")}
                placeholder="06:00"
                value={form.departureTimeFrom}
                onChange={(e) => set("departureTimeFrom", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                error={!!form.departureTimeFrom && !isHHmm(form.departureTimeFrom)}
                helperText={!!form.departureTimeFrom && !isHHmm(form.departureTimeFrom) ? "HH:mm" : " "}
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.timeTo", "Time to (HH:mm)")}
                placeholder="18:00"
                value={form.departureTimeTo}
                onChange={(e) => set("departureTimeTo", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                error={!!form.departureTimeTo && !isHHmm(form.departureTimeTo)}
                helperText={!!form.departureTimeTo && !isHHmm(form.departureTimeTo) ? "HH:mm" : " "}
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.totalSeatsMin", "Total Seats Min")}
                type="number"
                value={form.totalSeatsMin}
                onChange={(e) => set("totalSeatsMin", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.totalSeatsMax", "Total Seats Max")}
                type="number"
                value={form.totalSeatsMax}
                onChange={(e) => set("totalSeatsMax", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.adultsChdMin", "Adult+CHD Min")}
                type="number"
                value={form.adultsChdMin}
                onChange={(e) => set("adultsChdMin", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.adultsChdMax", "Adult+CHD Max")}
                type="number"
                value={form.adultsChdMax}
                onChange={(e) => set("adultsChdMax", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.infantsMin", "Infants Min")}
                type="number"
                value={form.infantsMin}
                onChange={(e) => set("infantsMin", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <Box gridColumn="span 3">
              <TextField
                label={t("app.main.flights.filters.infantsMax", "Infants Max")}
                type="number"
                value={form.infantsMax}
                onChange={(e) => set("infantsMax", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Box>

            <Box gridColumn="span 12">
              <TextField
                label={t("app.main.flights.filters.airlines", "Airline company IDs")}
                placeholder="uuid1, uuid2  uuid3"
                value={form.airlineCompanyIdsText}
                onChange={(e) => set("airlineCompanyIdsText", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                helperText={t(
                  "app.main.flights.filters.airlines.helper",
                  "Comma or space separated list of airline UUIDs"
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvOpen(false)}>
            {t("app.shared.cancel", "Cancel")}
          </Button>
          <Button variant="contained" color="primary" onClick={applyAdvanced}>
            {t("app.shared.apply", "Apply")}
          </Button>
        </DialogActions>
      </Dialog>

      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow className={classes.root}>
            <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
              {t("app.main.flight.multiTable.cell.flightNumber", "Flight Number")}
            </TableCell>
            <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
              {t("app.main.schedules.splitPassenger.cell.flight", "Flight")}
            </TableCell>
            <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
              {t("app.main.schedules.addPassenger.departureTime", "Departure time")}
            </TableCell>

            {hasUserAnyAuthority([ROLES.ROLE_ADMIN]) && (
              <>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  {t("app.main.flights.multilineTable.totalFree", "Total seats / Free seats")}
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  {t("app.main.flights.multilineTable.resInf", "Reserved seats / INF")}
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  {t("app.main.flights.multilineTable.loadFactor", "Load Factor")}
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  <Tooltip
                    title={`${t(
                      "app.main.flights.multilineTable.priceAdult.tooltip",
                      "Base fare (adult) configured for this route (one-way)."
                    )}`}
                    placement="top"
                    arrow
                  >
                    <span>{t("app.main.flights.multilineTable.priceAdult", "Price (adult)")}</span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  <Tooltip
                    title={`${t(
                      "app.main.flights.multilineTable.totalValue.tooltip",
                      "Total sales (realized): sum of actually sold fares (may include returns/children/dynamic pricing/upsells)."
                    )}`}
                    placement="top"
                    arrow
                  >
                    <span>{t("app.main.flights.multilineTable.totalValue", "Total sales value")}</span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  {t("app.main.flights.multilineTable.avgSoldSeat", "Avg. per sold seat")}
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  {t("app.main.flights.multilineTable.avgPerSeat", "Avg. per seat")}
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  {t("app.main.flights.multilineTable.provider", "Provider")}
                </TableCell>
                <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>
                  {t("app.main.employees.employeeList.table.actions", "Actions")}
                </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {props.loading ? (
            <LoadingTableRow
              colSpan={hasUserAnyAuthority([ROLES.ROLE_ADMIN]) ? ADMIN_COLS : NON_ADMIN_COLS}
            />
          ) : !props.data || props.data.content.length === 0 ? (
            <NoDataTableRow
              colSpan={hasUserAnyAuthority([ROLES.ROLE_ADMIN]) ? ADMIN_COLS : NON_ADMIN_COLS}
            />
          ) : (
            props.data.content.map((row: FlightRequestBody) => {
              const total = Number(row.maxNumberOfTravelers) || 0;
              const free = Number(row.numberOfAvailableSeats) || 0;
              const reserved = Math.max(0, total - free);
              const inf = (row as any)?.numberOfInfants ?? 0;
              const totalRevenue = Number((row as any)?.realization) || 0;
              const currency = row.currency?.symbol ?? "€";
              const adultPrice = getAdultPriceWithTax(row);
              const avgPerSoldSeat = reserved > 0 ? totalRevenue / reserved : 0;
              const avgPerSeat = total > 0 ? totalRevenue / total : 0;
              const loadPct = loadFactor(total, reserved);

              return (
                <React.Fragment key={row.id}>
                  <TableRow onClick={toggleOpen} className={classes.root}>
                    <TableCell style={{ textAlign: "center" }}>
                      <MultilineTableCell content={[`#${row.number}`]} />
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      <MultilineTableCell
                        content={[`${row.departureAirport.code} ➝ ${row.arrivalAirport.code}`]}
                      />
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      <MultilineTableCell
                        content={[DateUtils.toHumanDate(row.dateOfDeparture, row.timeOfDeparture)]}
                      />
                    </TableCell>

                    {hasUserAnyAuthority([ROLES.ROLE_ADMIN]) && (
                      <>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell
                            content={[
                              t("app.main.flights.multilineTable.seatsSummary", {
                                total,
                                reserved,
                                free,
                                defaultValue: "{{total}} total – {{reserved}} reserved = {{free}} free",
                              }),
                            ]}
                          />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell
                            content={[
                              <span key="res-inf">
                                <span style={{ marginRight: 6 }}>🔴 {reserved}</span>
                                <span>
                                  {t("app.main.flights.multilineTable.inf", "INF")}: {inf as number}
                                </span>
                              </span>,
                            ]}
                          />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell
                            content={[
                              <div key="bar" className={classes.loadWrap}>
                                <div className={classes.loadFill} style={{ width: `${loadPct}%` }} />
                              </div>,
                              <span key="label" className={classes.loadLabel}>
                                {loadPct}%
                              </span>,
                            ]}
                          />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell content={[formatMoney(adultPrice ?? 0, currency)]} />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell content={[formatMoney(totalRevenue, currency)]} />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell content={[formatMoney(avgPerSoldSeat, currency)]} />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell content={[formatMoney(avgPerSeat, currency)]} />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <MultilineTableCell content={[row.provider || "—"]} />
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <TableActionButtons
                            {...tableActions}
                            rowId={row.id}
                            name={`${row.departureAirport.name}(${row.departureAirport.code}) - ${row.arrivalAirport.name}(${row.arrivalAirport.code})`}
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>

      <ModalDialog
        open={modalOpen.state}
        title={`${t(
          "app.main.flights.flightList.deletePopup.primaryText",
          "Are you sure you want to delete flight"
        )} '${modalOpen.flightName}'?`}
        onOk={onOkModal}
        onCancel={() => setModalOpen({ state: false, deleteId: "", flightName: "" })}
      />
    </TableContainer>
  );
};

export default MultilineTable;
